import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges
} from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { S3AttachmentService } from 'src/app/shared/attachments/s3-attachment.service';
import {
  AIScribeUsageData,
  ApplyOutput,
  AssessmentAIScribeInput,
  AssessmentAIScribeResult,
  ConversationExtraction,
  FindingPair,
  FindingRow,
  SavedAIScribeData,
  ScribeMode,
  ScribeSession
} from './assessment-ai-scribe.model';
import { AssessmentAIScribeService } from './assessment-ai-scribe.service';
import { RecordState } from './components/scribe-record-tab/scribe-record-tab.component';

type Step = 'input' | 'loading' | 'results';
type InputTab = 'record' | 'type' | 'history';

const EXTRACTION_LOADING_PHRASES = [
  'aiScribe.loading.transcribing',
  'aiScribe.loading.identifyingSigns',
  'aiScribe.loading.extractingHistory',
  'aiScribe.loading.analyzingSymptoms',
  'aiScribe.loading.interpretingAssessment',
  'aiScribe.loading.matchingTreatment',
  'aiScribe.loading.finalizing'
];

const CONVERSATION_LOADING_PHRASES = [
  'aiScribe.loading.conv.processingAudio',
  'aiScribe.loading.conv.identifyingSpeakers',
  'aiScribe.loading.conv.generatingTranscript',
  'aiScribe.loading.conv.writingSummary',
  'aiScribe.loading.conv.composingDiscussion',
  'aiScribe.loading.conv.finalizing'
];

@Component({
  selector: 'csi-assessment-ai-scribe',
  templateUrl: './assessment-ai-scribe.component.html',
  styleUrls: ['./assessment-ai-scribe.component.scss'],
  providers: [AssessmentAIScribeService]
})
export class AssessmentAiScribeComponent implements OnChanges, OnDestroy {
  @Input() assessmentId: string;
  @Input() clinicId: string;
  @Input() enabledSections: string[];
  @Input() assessmentMethod: string | null = null;
  @Input() patientName: string | null = null;
  @Input() doctorName: string | null = null;
  @Input() set initialUsage(data: AIScribeUsageData | null) {
    if (data && !this.clinicUsage) {
      this.clinicUsage = data;
      if (this.tokenLimitReached || this.sessionLimitReached) {
        this.activeTab = 'history';
      }
      this.updateCanSubmit();
      this.cdr.markForCheck();
    }
  }
  @Input() set savedResult(data: SavedAIScribeData | null) {
    this.sessions = data && data.sessions ? [...data.sessions] : [];
    this.conversationResult = data && data.conversationResult ? data.conversationResult : null;
    this.expandedSessionTimestamps = new Set();
    this.cdr.markForCheck();
  }

  @Input() isExpanded = false;

  @Output() applied = new EventEmitter<ApplyOutput>();
  @Output() cancelled = new EventEmitter<void>();
  @Output() recordingAttachment = new EventEmitter<any>();
  /** Emitted when a session is archived (on Start Over) so the parent can auto-save. */
  @Output() sessionArchived = new EventEmitter<SavedAIScribeData>();
  @Output() expandToggle = new EventEmitter<void>();
  @Output() stepChange = new EventEmitter<string>();

  private _step: Step = 'input';
  get step(): Step {
    return this._step;
  }
  set step(value: Step) {
    this._step = value;
    this.stepChange.emit(value);
  }
  activeTab: InputTab = 'record';

  /** V2: active pipeline mode */
  activeMode: ScribeMode = 'extraction';
  /** V2: most recent applied conversation result for this visit */
  conversationResult: ConversationExtraction | null = null;
  /** Set to true before calling apply() from reapplySession so history is not duplicated */
  private skipNextHistoryEntry = false;

  recordState: RecordState = 'idle';
  recordSeconds = 0;
  formattedTime = '00:00';
  s3Key: string | null = null;
  bucketName = '';
  recordedS3Object: any = null;

  private mediaRecorder: any = null;
  private audioChunks: Blob[] = [];
  private timerRef: any = null;

  typedTranscript = '';
  liveTranscript = '';
  speechSupported = !!(
    (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
  );
  submittedInput = '';

  private speechRecognition: any = null;
  private speechFinalTranscript = '';

  clinicUsage: AIScribeUsageData | null = null;
  clinicUsageLoading = false;

  result: AssessmentAIScribeResult | null = null;
  rows: FindingRow[] = [];
  leftRows: FindingRow[] = [];
  rightRows: FindingRow[] = [];
  otherRows: FindingRow[] = [];
  pairedRows: FindingPair[] = [];
  activeRowCount = 0;
  errorMessage = '';

  canSubmit = false;
  isSubmitting = false;

  /** Full session log loaded from / being built toward SavedAIScribeData */
  sessions: ScribeSession[] = [];

  /** Timestamps of sessions whose detail body is currently expanded */
  expandedSessionTimestamps = new Set<string>();

  get sessionLimitReached(): boolean {
    if (!this.clinicUsage) {
      return false;
    }
    const dailyLimit = this.clinicUsage.limits.dailySessions;
    if (
      dailyLimit !== null &&
      dailyLimit !== undefined &&
      this.clinicUsage.today.requestCount >= dailyLimit
    ) {
      return true;
    }
    const monthlyLimit = this.clinicUsage.limits.monthlySessions;
    if (
      monthlyLimit !== null &&
      monthlyLimit !== undefined &&
      this.clinicUsage.thisMonth.requestCount >= monthlyLimit
    ) {
      return true;
    }
    return false;
  }

  get tokenLimitReached(): boolean {
    if (!this.clinicUsage) {
      return false;
    }
    const { dailyTokens, monthlyTokens } = this.clinicUsage.limits;
    return (
      (dailyTokens > 0 && this.clinicUsage.today.totalTokens >= dailyTokens) ||
      (monthlyTokens > 0 && this.clinicUsage.thisMonth.totalTokens >= monthlyTokens)
    );
  }

  get limitType(): 'daily' | 'monthly' | null {
    if (!this.clinicUsage) {
      return null;
    }
    // Daily session cap
    const dailySessionLimit = this.clinicUsage.limits.dailySessions;
    if (
      dailySessionLimit !== null &&
      dailySessionLimit !== undefined &&
      this.clinicUsage.today.requestCount >= dailySessionLimit
    ) {
      return 'daily';
    }
    // Monthly session cap
    const monthlySessionLimit = this.clinicUsage.limits.monthlySessions;
    if (
      monthlySessionLimit !== null &&
      monthlySessionLimit !== undefined &&
      this.clinicUsage.thisMonth.requestCount >= monthlySessionLimit
    ) {
      return 'monthly';
    }
    // Token limits (informational fallback)
    const { dailyTokens, monthlyTokens } = this.clinicUsage.limits;
    if (dailyTokens > 0 && this.clinicUsage.today.totalTokens >= dailyTokens) {
      return 'daily';
    }
    if (monthlyTokens > 0 && this.clinicUsage.thisMonth.totalTokens >= monthlyTokens) {
      return 'monthly';
    }
    return null;
  }

  get limitResetTime(): string {
    if (!this.limitType) {
      return '';
    }
    const now = new Date();
    // Use the browser's local timezone — doctor is at the clinic
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const fmt = (d: Date) =>
      d.toLocaleString('en-US', {
        timeZone: tz,
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    if (this.limitType === 'daily') {
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(0, 0, 0, 0);
      return fmt(tomorrow);
    } else {
      const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1, 0, 0, 0, 0);
      return fmt(nextMonth);
    }
  }

  get tokenUsageWarning(): boolean {
    if (!this.clinicUsage || this.tokenLimitReached) {
      return false;
    }
    const { dailyTokens, monthlyTokens } = this.clinicUsage.limits;
    return (
      (dailyTokens > 0 && this.clinicUsage.today.totalTokens / dailyTokens >= 0.8) ||
      (monthlyTokens > 0 && this.clinicUsage.thisMonth.totalTokens / monthlyTokens >= 0.8)
    );
  }

  get reversedSessions(): ScribeSession[] {
    return this.sessions.slice().reverse();
  }

  toggleSession(timestamp: string) {
    if (this.expandedSessionTimestamps.has(timestamp)) {
      this.expandedSessionTimestamps.delete(timestamp);
    } else {
      this.expandedSessionTimestamps.add(timestamp);
    }
  }

  isSessionExpanded(timestamp: string): boolean {
    return this.expandedSessionTimestamps.has(timestamp);
  }

  /** V2: true when a recording or upload is in progress — blocks mode switching */
  get isRecordingActive(): boolean {
    return (
      this.recordState === 'recording' ||
      this.recordState === 'requesting' ||
      this.recordState === 'uploading'
    );
  }

  /** V2: true when at least one extraction session has been applied this visit */
  get hasExtractionSessions(): boolean {
    return this.sessions.some(s => s.applied && (!s.mode || s.mode === 'extraction'));
  }

  /** V2: true when a conversation result has been applied this visit */
  get hasConversationResult(): boolean {
    return this.conversationResult !== null;
  }

  /** V2: phrases appropriate to the active mode */
  get loadingPhrases(): string[] {
    return this.activeMode === 'conversation'
      ? CONVERSATION_LOADING_PHRASES
      : EXTRACTION_LOADING_PHRASES;
  }

  get loadingSubKey(): string {
    return this.activeMode === 'conversation' ? 'aiScribe.loading.convSub' : 'aiScribe.loading.sub';
  }

  loadingPhrase = EXTRACTION_LOADING_PHRASES[0];
  loadingPhraseIndex = 0;
  private loadingPhraseRef: any = null;
  loadingTranscriptExpanded = false;

  private destroy$ = new Subject<void>();
  private speculativeSub: Subscription | null = null;
  private speculativeResult: AssessmentAIScribeResult | null = null;
  private speculativeError: string | null = null;

  ngOnChanges(changes: SimpleChanges) {
    if (changes.assessmentId) {
      this.updateCanSubmit();
    }
    if (changes.clinicId && this.clinicId) {
      this.loadUsage();
    }
  }

  loadUsage() {
    this.clinicUsageLoading = true;
    this.assessmentAIScribeService
      .fetchUsage(this.clinicId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        usage => {
          this.ngZone.run(() => {
            this.clinicUsage = usage;
            this.clinicUsageLoading = false;
            // Auto-switch to History tab if limits are hit so user can still review past sessions
            if (this.tokenLimitReached || this.sessionLimitReached) {
              this.activeTab = 'history';
            }
            this.cdr.markForCheck();
          });
        },
        () => {
          this.ngZone.run(() => {
            this.clinicUsageLoading = false;
            this.cdr.markForCheck();
          });
        }
      );
  }

  constructor(
    private assessmentAIScribeService: AssessmentAIScribeService,
    private s3AttachmentService: S3AttachmentService,
    private authService: AuthenticationService,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef
  ) {}

  setTab(tab: InputTab) {
    const prevTab = this.activeTab;
    this.activeTab = tab;
    if (this.step === 'results') {
      this.step = 'input';
    }
    // Only reset recording when switching between record ↔ type,
    // and only if no completed recording exists yet.
    if (tab !== 'history' && prevTab !== 'history' && this.recordState !== 'recorded') {
      this.resetRecord();
    }
    if (tab !== 'history') {
      this.updateCanSubmit();
    }
  }

  private setState(fn: () => void) {
    this.ngZone.run(() => {
      fn();
      this.cdr.markForCheck();
    });
  }

  private buildRecordingFilename(): string {
    const sanitize = (s: string | null | undefined) =>
      (s || '')
        .replace(/[^a-zA-Z0-9]/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '');

    const now = new Date();
    const date = now.toISOString().slice(0, 10); // YYYY-MM-DD
    const time = now
      .toTimeString()
      .slice(0, 8)
      .replace(/:/g, '-'); // HH-MM-SS

    const parts = [
      'CSI_AI_Scribe',
      sanitize(this.doctorName) || 'Doctor',
      sanitize(this.patientName) || 'Patient',
      date,
      time
    ];
    return parts.join('_') + '.webm';
  }

  startRecording() {
    if (this.recordState !== 'idle') {
      return;
    }

    this.setState(() => {
      this.recordState = 'requesting';
      this.errorMessage = '';
    });

    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then(stream => {
        this.audioChunks = [];
        this.mediaRecorder = new (window as any).MediaRecorder(stream);

        this.mediaRecorder.ondataavailable = (e: any) => {
          if (e.data && e.data.size > 0) {
            this.audioChunks.push(e.data);
          }
        };

        this.mediaRecorder.onstop = () => {
          stream.getTracks().forEach((t: any) => t.stop());

          if (!this.clinicId) {
            this.setState(() => {
              this.errorMessage = 'Upload failed. Please try again.';
              this.recordState = 'idle';
            });
            return;
          }

          this.setState(() => {
            this.recordState = 'uploading';
          });

          const blob = new Blob(this.audioChunks, { type: 'audio/webm' });
          const filename = this.buildRecordingFilename();
          const file = new File([blob], filename, { type: 'audio/webm' });
          this.audioChunks = [];
          this.authService
            .getIdentityId()
            .pipe(
              switchMap(identityId => this.s3AttachmentService.uploadRecording(file, identityId)),
              takeUntil(this.destroy$)
            )
            .subscribe({
              next: (s3Object: any) => {
                this.setState(() => {
                  this.recordedS3Object = s3Object;
                  this.s3Key = s3Object.key || null;
                  this.bucketName = s3Object.bucket || '';
                  this.recordState = 'recorded';
                  this.updateCanSubmit();
                });
                // Start Gemini processing immediately while the doctor reviews
                this.startSpeculativeExtraction(s3Object.key, s3Object.bucket);
              },
              error: (err: any) => {
                console.error('[AiScribe] upload error', err);
                this.setState(() => {
                  this.errorMessage = 'Upload failed. Please try again.';
                  this.recordState = 'idle';
                });
              }
            });
        };

        this.mediaRecorder.start(100);
        this.startSpeechRecognition();

        if (this.timerRef) {
          clearInterval(this.timerRef);
        }

        this.setState(() => {
          this.recordState = 'recording';
          this.recordSeconds = this.activeMode === 'conversation' ? 300 : 180;
          this.formattedTime = this.formatSeconds(this.recordSeconds);
        });

        this.timerRef = setInterval(() => {
          this.setState(() => {
            this.recordSeconds--;
            this.formattedTime = this.formatSeconds(this.recordSeconds);

            // Auto-stop when countdown reaches 0
            if (this.recordSeconds <= 0) {
              this.stopRecording();
            }
          });
        }, 1000);
      })
      .catch(() => {
        this.setState(() => {
          this.recordState = 'idle';
          this.errorMessage = 'Microphone access denied.';
        });
      });
  }

  private startSpeechRecognition() {
    const SpeechRecognition =
      (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) return;

    this.speechFinalTranscript = '';
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onresult = (event: any) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          this.speechFinalTranscript += event.results[i][0].transcript + ' ';
        } else {
          interim += event.results[i][0].transcript;
        }
      }
      this.setState(() => {
        this.liveTranscript = (this.speechFinalTranscript + interim).trim();
      });
    };

    recognition.onend = () => {
      if (this.recordState === 'recording') {
        recognition.start();
      }
    };

    recognition.onerror = () => {};
    recognition.start();
    this.speechRecognition = recognition;
  }

  stopRecording() {
    if (this.timerRef) {
      clearInterval(this.timerRef);
      this.timerRef = null;
    }
    if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
      this.mediaRecorder.stop();
    }
    if (this.speechRecognition) {
      this.speechRecognition.onend = null;
      this.speechRecognition.stop();
      this.speechRecognition = null;
    }
  }

  resetRecord() {
    this.stopRecording();
    this.recordState = 'idle';
    this.recordSeconds = this.activeMode === 'conversation' ? 300 : 180;
    this.formattedTime = this.activeMode === 'conversation' ? '05:00' : '03:00';
    this.s3Key = null;
    this.bucketName = '';
    this.recordedS3Object = null;
    this.audioChunks = [];
    this.mediaRecorder = null;
    this.liveTranscript = '';
    this.speechFinalTranscript = '';
    this.clearSpeculative();
    this.updateCanSubmit();
  }

  /** V2: switch between extraction and conversation pipelines */
  setMode(mode: ScribeMode) {
    if (this.isRecordingActive) {
      return;
    }
    this.activeMode = mode;
    this.cdr.markForCheck();
  }

  private startSpeculativeExtraction(s3Key: string, bucketName: string) {
    if (!this.assessmentId || !s3Key) return;
    this.clearSpeculative();
    const input: AssessmentAIScribeInput = {
      assessmentId: this.assessmentId,
      s3Key,
      bucketName,
      enabledSections: this.enabledSections,
      mode: this.activeMode,
      assessmentMethod: this.assessmentMethod
    };
    this.speculativeSub = this.assessmentAIScribeService
      .extract(input)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: result => {
          this.speculativeResult = result;
          // If the doctor already clicked Submit, deliver immediately
          if (this.step === 'loading') {
            this.deliverResult(result);
          }
        },
        error: err => {
          const raw: string = (err && err.message) || '';
          let friendly = 'AI Scribe failed. Please try again.';
          if (/daily session limit/i.test(raw)) {
            friendly = 'Daily session limit reached. Please try again tomorrow.';
          } else if (/monthly session limit/i.test(raw)) {
            friendly = 'Monthly session limit reached. Please try again next month.';
          } else if (/daily token limit/i.test(raw)) {
            friendly = 'Daily limit reached. Please try again tomorrow.';
          } else if (/monthly token limit/i.test(raw)) {
            friendly = 'Monthly limit reached. Please try again next month.';
          } else if (/quota/i.test(raw) || /limit/i.test(raw)) {
            friendly = 'Usage limit reached. Please try again later.';
          } else if (/timed out/i.test(raw)) {
            friendly = 'Extraction timed out. Please try again.';
          }
          this.speculativeError = friendly;
          if (this.step === 'loading') {
            this.stopLoadingPhrases();
            this.setState(() => {
              this.errorMessage = this.speculativeError;
              this.step = 'results';
              this.isSubmitting = false;
            });
          }
          if (this.clinicId) {
            this.loadUsage();
          }
        }
      });
  }

  private clearSpeculative() {
    if (this.speculativeSub) {
      this.speculativeSub.unsubscribe();
      this.speculativeSub = null;
    }
    this.speculativeResult = null;
    this.speculativeError = null;
  }

  private startLoadingPhrases() {
    this.loadingPhraseIndex = 0;
    this.loadingPhrase = this.loadingPhrases[0];
    if (this.loadingPhraseRef) {
      clearInterval(this.loadingPhraseRef);
    }
    this.loadingPhraseRef = setInterval(() => {
      this.setState(() => {
        this.loadingPhraseIndex = (this.loadingPhraseIndex + 1) % this.loadingPhrases.length;
        this.loadingPhrase = this.loadingPhrases[this.loadingPhraseIndex];
      });
    }, 2800);
  }

  private stopLoadingPhrases() {
    if (this.loadingPhraseRef) {
      clearInterval(this.loadingPhraseRef);
      this.loadingPhraseRef = null;
    }
  }

  private deliverResult(result: AssessmentAIScribeResult) {
    this.stopLoadingPhrases();
    // Guard: findings may be absent in conversation-mode responses
    if (!result.findings) {
      result.findings = [];
    }
    this.setState(() => {
      this.result = result;
      this.rows = this.buildRows(result);
      this.step = 'results';
      this.isSubmitting = false;
    });
  }

  onTranscriptChange() {
    this.updateCanSubmit();
  }

  submit() {
    this.skipNextHistoryEntry = false;
    this.isSubmitting = true;
    this.step = 'loading';
    this.loadingTranscriptExpanded = false;
    this.errorMessage = '';
    this.submittedInput =
      this.activeTab === 'record' ? this.liveTranscript || '' : this.typedTranscript.trim();

    // If speculative already finished — deliver instantly, no loading screen needed
    if (this.activeTab === 'record' && this.speculativeResult) {
      this.deliverResult(this.speculativeResult);
      this.clearSpeculative();
      return;
    }

    this.startLoadingPhrases();

    // Cancel any in-flight speculative and run a fresh extraction
    this.clearSpeculative();

    const input: AssessmentAIScribeInput =
      this.activeTab === 'record'
        ? {
            assessmentId: this.assessmentId,
            s3Key: this.s3Key,
            bucketName: this.bucketName,
            enabledSections: this.enabledSections,
            mode: this.activeMode,
            assessmentMethod: this.activeMode === 'conversation' ? null : this.assessmentMethod
          }
        : {
            assessmentId: this.assessmentId,
            transcript: this.typedTranscript.trim(),
            enabledSections: this.enabledSections,
            mode: this.activeMode,
            assessmentMethod: this.activeMode === 'conversation' ? null : this.assessmentMethod
          };

    this.assessmentAIScribeService
      .extract(input)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        result => {
          this.deliverResult(result);
          // Refresh usage after extraction completes
          if (this.clinicId) {
            this.loadUsage();
          }
        },
        err => {
          this.stopLoadingPhrases();
          const raw: string = (err && err.message) || '';
          // Map known Lambda errors to friendly messages; hide GraphQL internals
          let friendly = 'AI Scribe failed. Please try again.';
          if (/daily session limit/i.test(raw)) {
            friendly = `Daily session limit reached. Resets at ${this.limitResetTime ||
              'midnight'}.`;
          } else if (/monthly session limit/i.test(raw)) {
            friendly = `Monthly session limit reached. Resets at ${this.limitResetTime ||
              'start of next month'}.`;
          } else if (/daily token limit/i.test(raw)) {
            friendly = `Daily limit reached. Resets at ${this.limitResetTime || 'midnight'}.`;
          } else if (/monthly token limit/i.test(raw)) {
            friendly = `Monthly limit reached. Resets at ${this.limitResetTime ||
              'start of next month'}.`;
          } else if (/quota/i.test(raw) || /limit/i.test(raw)) {
            friendly = 'Usage limit reached. Please try again later.';
          } else if (/timed out/i.test(raw)) {
            friendly = 'Extraction timed out. Please try again.';
          } else if (/upload failed/i.test(raw)) {
            friendly = 'Upload failed. Please try again.';
          }
          this.setState(() => {
            this.errorMessage = friendly;
            this.step = 'results';
            this.isSubmitting = false;
          });
          if (this.clinicId) {
            this.loadUsage();
          }
        }
      );
  }

  private buildRows(result: AssessmentAIScribeResult): FindingRow[] {
    // Handle case where findings is undefined or null
    if (!result.findings) {
      result.findings = [];
    }

    const rows = result.findings.map(f => this.createRow(f));
    this.leftRows = rows.filter(r => r.original.systemKey.startsWith('LEFT_'));
    this.rightRows = rows.filter(r => r.original.systemKey.startsWith('RIGHT_'));
    this.otherRows = rows.filter(
      r => !r.original.systemKey.startsWith('LEFT_') && !r.original.systemKey.startsWith('RIGHT_')
    );
    this.pairedRows = this.buildPairs(this.leftRows, this.rightRows);
    this.activeRowCount = rows.filter(r => !r.isDeleted).length;
    return rows;
  }

  private buildPairs(left: FindingRow[], right: FindingRow[]): FindingPair[] {
    const pairMap = new Map<string, FindingPair>();
    const order: string[] = [];

    left.forEach(row => {
      const key = row.original.systemKey.replace(/^LEFT_/, '');
      if (!pairMap.has(key)) {
        pairMap.set(key, { signKey: key });
        order.push(key);
      }
      pairMap.get(key).left = row;
    });

    right.forEach(row => {
      const key = row.original.systemKey.replace(/^RIGHT_/, '');
      if (!pairMap.has(key)) {
        pairMap.set(key, { signKey: key });
        order.push(key);
      }
      pairMap.get(key).right = row;
    });

    return order.map(k => pairMap.get(k));
  }

  private createRow(f: any): FindingRow {
    const row: FindingRow = {
      original: f,
      editedValue: f.normalizedValue,
      isDeleted: false,
      isEditing: false,
      isModified: false,
      displayValue: '',
      displayLabel: f.systemKey.replace(/^(LEFT|RIGHT)_/, '').replace(/_/g, ' '),
      reasonLabels: f.confidenceReasons
    };
    row.displayValue = this.computeDisplayValue(row);
    return row;
  }

  private computeDisplayValue(row: FindingRow): string {
    const unit = row.original.system_unit;
    const val = this.titleCaseValue(row.editedValue);
    return unit ? val + ' ' + unit : val;
  }

  private titleCaseValue(val: string): string {
    if (!val) return val;
    if (!isNaN(Number(val))) return val;
    return val.replace(/\b\w/g, c => c.toUpperCase());
  }

  toggleDelete(row: FindingRow) {
    row.isDeleted = !row.isDeleted;
    this.activeRowCount = this.rows.filter(r => !r.isDeleted).length;
  }

  apply(removedFields: string[] = []) {
    const skipHistoryEntry = this.skipNextHistoryEntry;
    this.skipNextHistoryEntry = false;

    if (this.activeMode === 'conversation') {
      this.applyConversation(skipHistoryEntry);
      return;
    }
    const findings: Record<string, string> = {};
    const findingUnits: Record<string, string> = {};

    this.rows
      .filter(r => !r.isDeleted)
      .forEach(r => {
        findings[r.original.systemKey] = r.editedValue;
        if (r.original.system_unit) {
          findingUnits[r.original.systemKey] = r.original.system_unit;
        }
      });

    // Pull impressions and treatment plan from the structured assessment section (v9.0.0+)
    const scribeAssessment = this.result && this.result.assessment;
    const impressions = (scribeAssessment && scribeAssessment.impressions) || undefined;
    const treatmentPlan = (scribeAssessment && scribeAssessment.treatmentPerformed) || undefined;

    const updatedSessions = skipHistoryEntry
      ? this.sessions
      : [
          ...this.sessions,
          {
            timestamp: new Date().toISOString(),
            browserTranscript: this.submittedInput,
            result: this.result!,
            applied: true
          } as ScribeSession
        ];

    const output: ApplyOutput = {
      findings,
      findingUnits,
      transcript: this.result ? this.result.transcript : null,
      savedData: { sessions: updatedSessions, conversationResult: this.conversationResult || null },
      impressions,
      treatmentPlan,
      history: (this.result && this.result.history) || null,
      assessment: scribeAssessment || null,
      symptoms: (this.result && this.result.symptoms) || null,
      patientProfile: (this.result && this.result.patientProfile) || null,
      patientFile: (this.result && this.result.patientFile) || null,
      dewsScreening: (this.result && this.result.dewsScreening) || null,
      removedFields
    };

    this.recordingAttachment.emit(this.recordedS3Object || null);
    this.applied.emit(output);
  }

  /** V2: apply handler for conversation mode — stores transcript/summary, no form-field patching */
  private applyConversation(skipHistoryEntry = false) {
    const conversation = this.result && this.result.conversation ? this.result.conversation : null;

    const updatedSessions = skipHistoryEntry
      ? this.sessions
      : [
          ...this.sessions,
          {
            timestamp: new Date().toISOString(),
            browserTranscript: this.submittedInput,
            result: this.result,
            mode: 'conversation',
            applied: true
          } as ScribeSession
        ];

    const output: ApplyOutput = {
      findings: {},
      findingUnits: {},
      transcript: this.result ? this.result.transcript : null,
      savedData: {
        sessions: updatedSessions,
        conversationResult: conversation
      },
      conversationResult: conversation
    };

    this.recordingAttachment.emit(this.recordedS3Object || null);
    this.applied.emit(output);
  }

  reset() {
    // Archive the current result (if any) to the session log before clearing
    if (this.result) {
      const session: ScribeSession = {
        timestamp: new Date().toISOString(),
        browserTranscript: this.submittedInput,
        result: this.result,
        applied: false
      };
      this.sessions = [...this.sessions, session];
      this.sessionArchived.emit({ sessions: this.sessions });
    }

    this.step = 'input';
    this.activeTab = 'record';
    this.result = null;
    this.rows = [];
    this.leftRows = [];
    this.rightRows = [];
    this.otherRows = [];
    this.pairedRows = [];
    this.activeRowCount = 0;
    this.errorMessage = '';
    this.typedTranscript = '';
    this.submittedInput = '';
    this.recordedS3Object = null;
    this.isSubmitting = false;
    this.clearSpeculative();
    this.resetRecord();
  }

  private updateCanSubmit() {
    if (!this.assessmentId || this.sessionLimitReached || this.tokenLimitReached) {
      this.canSubmit = false;
      return;
    }
    if (this.activeTab === 'record') {
      this.canSubmit = this.recordState === 'recorded' && !!this.s3Key;
    } else {
      this.canSubmit = this.typedTranscript.trim().length > 0;
    }
  }

  reapplySession(session: ScribeSession) {
    this.activeMode = session.mode || 'extraction';
    const result = session.result;
    if (!result.findings) {
      result.findings = [];
    }
    this.result = result;
    this.rows = this.buildRows(result);
    this.submittedInput = session.browserTranscript;
    this.errorMessage = '';
    this.step = 'results';
    this.skipNextHistoryEntry = true;
    this.cdr.markForCheck();
  }

  formatSessionKey(key: string): string {
    return key
      .replace('LEFT_', 'L·')
      .replace('RIGHT_', 'R·')
      .replace(/_/g, ' ');
  }

  private formatSeconds(total: number): string {
    const m = Math.floor(total / 60)
      .toString()
      .padStart(2, '0');
    const s = (total % 60).toString().padStart(2, '0');
    return m + ':' + s;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.timerRef) {
      clearInterval(this.timerRef);
    }
    this.stopLoadingPhrases();
    if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
      this.mediaRecorder.stop();
    }
    if (this.speechRecognition) {
      this.speechRecognition.onend = null;
      this.speechRecognition.stop();
    }
  }
}
